import { useEffect, useState } from 'react';
import { Table } from 'antd';
import { Button, SIZE, Stack } from '@quinyx/ui';

import {
  deleteSystemAdminToken,
  fetchSystemAdminToken,
  getSystemAdminToken,
} from '../../../api/endpoints/support/system-admin-token';
import { withAdminLayout } from '../../../layouts/admin';
import { useTranslate } from '../../../hooks/translate';
import styles from './index.module.scss';

const SystemAdminToken = () => {
  const { t } = useTranslate();
  const [token, setToken] = useState<string | null>();
  const [error, setError] = useState<number | null>(null);

  const handleFetch = async () => {
    try {
      setError(null);
      const data = await fetchSystemAdminToken();
      setToken(data);
    } catch (e: any) {
      console.error(e);
      setError(e.response.data.status);
    }
  };

  const handleGet = async () => {
    try {
      setError(null);
      const data = await getSystemAdminToken();
      setToken(data);
    } catch (e: any) {
      console.error(e);
      setError(e.response.data.status);
    }
  };

  const handleDelete = async () => {
    try {
      setError(null);
      if (token) {
        await deleteSystemAdminToken(token);
        setToken(null);
      }
    } catch (e: any) {
      console.error(e);
      setError(e.response.data.status);
    }
  };

  const handleCopy = () => token && navigator.clipboard.writeText(token);

  const columns = [
    {
      title: t('admin.systemAdminToken.token'),
      dataIndex: 'data',
      key: 'data',
      render: (data: string) => (
        <Stack direction="row" spacing={SIZE.S} alignItems="center">
          {data}{' '}
          <Button
            onClick={handleCopy}
            data-test-id="copy-button"
            aria-label={t('common.copy')}
            icon="copy"
            variant="text"
          />
        </Stack>
      ),
    },
    {
      title: t('common.delete'),
      dataIndex: 'delete',
      key: 'delete',
      render: () => (
        <Button
          onClick={handleDelete}
          data-test-id="delete-button"
          aria-label={t('common.delete')}
          icon="delete"
          variant="text"
          color="error"
        />
      ),
    },
  ];

  useEffect(() => {
    handleFetch();
  }, []);

  return (
    <div className={styles.page}>
      <h2>{t('admin.systemAdminToken.title')}</h2>
      <p>{t('admin.systemAdminToken.description')}</p>
      <p>
        <i>{t('admin.systemAdminToken.ssoNote')}</i>
      </p>
      <Button
        data-test-id="generate-token-button"
        onClick={handleGet}
        disabled={error === 403}
        text={
          token
            ? t('admin.systemAdminToken.generateNewToken')
            : t('admin.systemAdminToken.generateToken')
        }
      />

      {token && (
        <Table
          columns={columns}
          dataSource={[
            {
              key: token,
              data: token,
            },
          ]}
          pagination={false}
        />
      )}

      {error && (
        <p style={{ color: 'red' }}>
          {error === 403
            ? t('admin.systemAdminToken.ssoRequired')
            : t('admin.systemAdminToken.error')}
        </p>
      )}
    </div>
  );
};

export default withAdminLayout(SystemAdminToken);
